import { minutesSince, tt } from '@agos/shared'
/**
 * The receipt a re-opened table invalidates — `FRONTEND.md` §9.34.
 *
 * **This file used to hold the round logic too, and nothing ever called it.** `lockedLinesFrom`,
 * `newRoundLines`, `hasNewRound` and `kitchenRoundFor` shipped with *"the till, and manager-orders is
 * complete"* and had no caller from that commit to the day they were removed. The till does not need
 * them: `RestaurantPage.tsx:138` sends a waiter to `/new-order?…&edit=<id>`, and rounds are
 * `order-builder/order-edit.ts` — which reads the order's own `maxRound`, locks every line below it,
 * and sends new food as `newItems`, so the ticket the kitchen is working from is never deleted and
 * re-inserted.
 *
 * They were also worth not keeping: `kitchenRoundFor` answered at most `2`, so a table on its third
 * round would have been labelled its second. `order-edit.ts` has no such ceiling.
 *
 * The rules they tested are still tested, against the module that is wired — `cart.test.ts` asserts
 * that a new tap never merges into a locked line, and that a locked line can be neither changed nor
 * removed.
 */

/**
 * Whether re-opening this order changes what was already charged.
 *
 * A receipt is printed per payment, not per order, so adding to a table that
 * has already had one printed means the first receipt no longer describes what
 * was sold. The waiter is offered a storno; nothing happens automatically,
 * because voiding a fiscal receipt is a tax event.
 */
export function needsReceiptStorno(order: {
  fiskalPrintedAt?: string | null
  fiskalStornoAt?: string | null
}): boolean {
  return Boolean(order.fiskalPrintedAt) && !order.fiskalStornoAt
}

/**
 * How long a table has been open, as the card says it — §9.126, the owner's point 11b.
 *
 * <p><b>A waiter reads this to know which table has been sitting.</b> The card showed a total and no
 * time, which cannot answer *which of these needs me* — the one question the screen exists for.
 *
 * <p>Under a minute is *práve teraz* rather than `0 min`, as legacy words it: a bill that has just
 * been opened is not a bill that has been waiting no time, and the zero reads as a missing value.
 */
export function openForNote(createdAt: string | undefined, now: number): string {
  if (createdAt === undefined || createdAt === '') return ''
  const minutes = minutesSince(createdAt, now)
  if (!Number.isFinite(minutes) || minutes < 0) return ''
  if (minutes < 1) return tt('práve teraz')
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  return `${hours} h ${minutes % 60} min`
}

/**
 * The note a customer left on the order - §9.375, FB-669.
 *
 * **A missed port.** Legacy's open-table card draws it with a 📝 (`restaurant.tsx:354`); AGOS drew
 * the table, the lines and the total and never the note, so *bez cibule* reached the kitchen ticket
 * and never the person carrying the plate. The owner asked for it on both screens they work from -
 * *Otvorené stoly* and the table map.
 *
 * **Trimmed, and a blank is nothing.** A note of spaces is what an empty `<textarea>` submit leaves
 * behind, and an empty line with a 📝 on it reads as a note somebody has not been told.
 *
 * A function rather than `order.notes && …` inline twice, because two screens now ask the same
 * question and one of them would otherwise learn the answer differently.
 */
export function noteOf(order: { notes?: string | null }): string {
  return typeof order.notes === 'string' ? order.notes.trim() : ''
}
